import { getExplicitShapePreset } from '@/lib/utils/dimensions';
import { getCircleEllipseParams, getRoundedRectParams } from '@/lib/utils/shape-paths';
import { appendCutContourLayerToDataUrl } from '@/lib/printcom/cut-contour-pdf';

export type PrintRaster = { dataURL: string; pixelWidth: number; pixelHeight: number; dpi: number | null };
export type PrintPage = { width: number; height: number; bleed: { left: number; right: number; top: number; bottom: number }; productName: string; sizeValue?: string;
  /** Spot colour name of the cut line (Probo "Cutcontour", others "CutContour"). */
  cutSeparationName?: string;
  /** Contour cut around the design: closed paths in page mm, top-left origin (src/lib/editor/contour-cut.ts). */
  contourPaths?: Array<Array<{ x: number; y: number }>>;
  /**
   * Corner radius in mm for the "rounded corners" shape preset
   * (`|shape:rounded_corners` in sizeValue): a rounded rectangle at trim size
   * goes into the file as the cut line.
   */
  cornerRadiusMm?: number;
};

/** One PDF writer for saved editor files, downloads and resumed admin exports. */
export async function createProductionPdf(raster: PrintRaster, page: PrintPage) {
  if (!(page.width > 0 && page.height > 0)) throw new Error('missing_print_size');
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: page.width > page.height ? 'landscape' : 'portrait',
    unit: 'mm', format: [page.width, page.height], compress: true, putOnlyUsedFonts: true });
  const trimWidthMm = Math.max(0, page.width - page.bleed.left - page.bleed.right);
  const trimHeightMm = Math.max(0, page.height - page.bleed.top - page.bleed.bottom);
  // Explicit page boxes keep bleed distinct from the complete media page.
  const context = (pdf.internal as any).getPageInfo(1).pageContext;
  context.trimBox = { bottomLeftX: page.bleed.left * 72 / 25.4,
    bottomLeftY: page.bleed.bottom * 72 / 25.4,
    topRightX: (page.width - page.bleed.right) * 72 / 25.4,
    topRightY: (page.height - page.bleed.top) * 72 / 25.4 };
  // The whole page is bleed: trim size plus bleed on every side.
  context.bleedBox = { bottomLeftX: 0, bottomLeftY: 0, topRightX: page.width * 72 / 25.4, topRightY: page.height * 72 / 25.4 };
  pdf.addImage(raster.dataURL, raster.dataURL.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG',
    0, 0, page.width, page.height, undefined, 'FAST');
  let dataURL = pdf.output('datauristring');
  const shapePreset = getExplicitShapePreset(page.sizeValue || '');
  // Only callers with the rounded corners preset pass a radius (the editor and
  // the resume export from its meta), so the radius alone decides.
  const roundedCornerCut = !page.contourPaths?.length && shapePreset !== 'circle'
    && Number(page.cornerRadiusMm) > 0 && trimWidthMm > 0 && trimHeightMm > 0
    ? getRoundedRectParams({ left: page.bleed.left, top: page.bleed.top, width: trimWidthMm, height: trimHeightMm }, Number(page.cornerRadiusMm))
    : null;
  if (page.contourPaths?.length) {
    // The cut follows the design; the raster under it (background colour or
    // design) runs on past the cut line into the bleed.
    dataURL = appendCutContourLayerToDataUrl(dataURL, { pageHeightMm: page.height,
      paths: page.contourPaths, separationName: page.cutSeparationName });
  } else if (shapePreset === 'circle' && trimWidthMm > 0 && trimHeightMm > 0) {
    dataURL = appendCutContourLayerToDataUrl(dataURL, { pageHeightMm: page.height, separationName: page.cutSeparationName,
      ellipse: getCircleEllipseParams({ left: page.bleed.left, top: page.bleed.top, width: trimWidthMm, height: trimHeightMm }) });
  } else if (roundedCornerCut) {
    dataURL = appendCutContourLayerToDataUrl(dataURL, { pageHeightMm: page.height, separationName: page.cutSeparationName,
      roundedRect: roundedCornerCut });
  }
  const base = page.productName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'ontwerp';
  return { dataURL, fileName: `${base}-${Number(page.width.toFixed(1))}x${Number(page.height.toFixed(1))}mm-drukbestand.pdf`,
    mimeType: 'application/pdf', pageWidthMm: page.width, pageHeightMm: page.height,
    trimWidthMm, trimHeightMm, bleedMm: { ...page.bleed }, pixelWidth: raster.pixelWidth,
    ...(page.contourPaths?.length ? { cutContour: { separationName: page.cutSeparationName || 'CutContour', paths: page.contourPaths.length } } : {}),
    ...(roundedCornerCut ? { cutContour: { separationName: page.cutSeparationName || 'CutContour', paths: 1, cornerRadiusMm: roundedCornerCut.radius } } : {}),
    pixelHeight: raster.pixelHeight, dpi: raster.dpi, generatedAt: new Date().toISOString() };
}
